import React from 'react';

import { BuildStatusCard } from 'src/components/build-status-card';
import CurrentDiffFileTree from 'src/components/diff-set/diff-file-tree';
import { withRunPipelineModal } from 'src/components/pipelines/pages/modals/run-pipeline-modal';
import {
  CollapseGpgKeyCard,
  ExpandGpgKeyCard,
} from 'src/components/settings/gpg-keys/components/gpg-key-card';
import { Cards, SidebarContent } from 'src/components/sidebar';
import { Commit } from 'src/components/types';
import SidebarTabs from 'src/sections/global/components/sidebar-tabs';
import Sidebar from 'src/sections/global/containers/sidebar';
import SidebarKeyboardShortcuts from 'src/sections/repository/components/sidebar-keyboard-shortcuts';

import {
  useSidebarExpandedWidth,
  useIsCollapsed,
  useResizeSidebar,
  useToggleSidebar,
  useSidebarTabsDetails,
} from '../hooks/sidebar-hooks';
import { AnnotatedRefData } from '../utils/use-fetch-annotated';

import {
  CollapseCommitBranches,
  ExpandCommitBranches,
} from './commit-branches';
import { CommitBuildStatusProvider } from './commit-build-provider';
import { CollapseCommitConnect, ExpandCommitConnect } from './commit-connect';
import { ExpandCommitParents, CollapseCommitParents } from './commit-parents';
import {
  ExpandCommitParticipants,
  CollapseCommitParticipants,
} from './commit-participants';
import { ExpandTagCard, CollapseTagCard } from './commit-tags';

export function CommitSidebar({
  commit,
  annotatedRefs,
}: {
  commit: Commit;
  annotatedRefs: AnnotatedRefData;
}): JSX.Element {
  // sidebar entries should be listed in the order they will be displayed,
  // and should be identical between the expanded and collapsed sidebar
  const expandedContent: SidebarContent[] = [
    {
      type: Cards.GpgKeyVerification,
      content: <ExpandGpgKeyCard commit={commit} />,
    },
    {
      type: Cards.BuildStatus,
      content: <BuildStatusCard />,
    },
    {
      type: Cards.CommitCard,
      content: <ExpandCommitParticipants commit={commit} />,
    },
    {
      type: Cards.CommitCard,
      content: <ExpandCommitParents commit={commit} />,
    },
    {
      type: Cards.CommitCard,
      content: <ExpandCommitBranches annotatedRefs={annotatedRefs} />,
    },
    {
      type: Cards.CommitCard,
      content: <ExpandTagCard commit={commit} />,
    },
    {
      type: Cards.CommitCard,
      content: <ExpandCommitConnect commit={commit} />,
    },
  ];
  const collapsedContent: SidebarContent[] = [
    {
      type: Cards.GpgKeyVerification,
      content: <CollapseGpgKeyCard commit={commit} />,
    },
    {
      type: Cards.BuildStatus,
      content: <BuildStatusCard isCollapsed />,
    },
    {
      type: Cards.CommitCard,
      content: <CollapseCommitParticipants commit={commit} />,
    },
    {
      type: Cards.CommitCard,
      content: <CollapseCommitParents commit={commit} />,
    },
    {
      type: Cards.CommitCard,
      content: <CollapseCommitBranches annotatedRefs={annotatedRefs} />,
    },
    {
      type: Cards.CommitCard,
      content: <CollapseTagCard commit={commit} />,
    },
    {
      type: Cards.CommitCard,
      content: <CollapseCommitConnect />,
    },
  ];

  const tabs = (
    <SidebarTabs
      key="sidebarTabs"
      expandedContent={expandedContent}
      expandedFileContent={<CurrentDiffFileTree />}
      sidebarTabsDetails={useSidebarTabsDetails}
      sidebarType="commit"
    />
  );

  return (
    <SidebarKeyboardShortcuts
      sidebarType="commit"
      toggleSidebar={useToggleSidebar()}
    >
      <CommitBuildStatusProvider commit={commit}>
        <Sidebar
          expandedWidth={useSidebarExpandedWidth()}
          collapsedContent={collapsedContent}
          expandedContent={expandedContent}
          isCollapsed={!!useIsCollapsed()}
          tabs={tabs}
          onResize={useResizeSidebar()}
        />
      </CommitBuildStatusProvider>
    </SidebarKeyboardShortcuts>
  );
}

export default withRunPipelineModal(
  CommitSidebar,
  'runPipelineFromCommitSidebar'
);
